import React, { useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  Group,
  ScrollArea,
  SegmentedControl,
  Stack,
  Table,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { IconArrowDownRight, IconArrowUpRight } from "@tabler/icons";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import BarChart from "./chartWidget/BarChart";
import { useFormatter } from "../hooks/useFomatter";
import {
  buildFooterTotals,
  groupChannelsByTeam,
  groupChannelsByTopLine,
  NO_TEAM_GROUP_ID,
} from "../helpers/trChannelPerformanceGrouping";
import SimpleLoadingIndicator from "./simpleLoadingIndicator";

const numFormatter = Intl.NumberFormat("en", { notation: "compact" });

const formatCompactCurrency = (value) => {
  if (value == null || Number.isNaN(Number(value))) return "0";
  return `${numFormatter.format(Number(value))}`;
};

const YoyGrowthCell = ({ value }) => {
  if (value == null || Number.isNaN(Number(value))) {
    return <Text size="xs">-</Text>;
  }

  const isPositive = Number(value) >= 0;
  const Icon = isPositive ? IconArrowUpRight : IconArrowDownRight;
  const color = isPositive ? "green" : "red";
  const prefix = Number(value) > 0 ? "+" : "";

  return (
    <Group spacing={4} noWrap>
      <Icon size={14} color={color} />
      <Text size="xs" color={color}>
        {prefix}
        {Number(value).toFixed(1)}%
      </Text>
    </Group>
  );
};

const TOP_CHART_LIMIT = 12;

const METRIC_GROUPS = [
  {
    key: "grossPremium",
    lyLabel: "dashboard.trChannelPerformance.lyPremium",
    cyLabel: "dashboard.trChannelPerformance.cyPremium",
  },
  {
    key: "brokerage",
    lyLabel: "dashboard.trChannelPerformance.lyBrokerage",
    cyLabel: "dashboard.trChannelPerformance.cyBrokerage",
  },
  {
    key: "trCommission",
    lyLabel: "dashboard.trChannelPerformance.lyTrCommission",
    cyLabel: "dashboard.trChannelPerformance.cyTrCommission",
  },
  {
    key: "profit",
    lyLabel: "dashboard.trChannelPerformance.lyProfit",
    cyLabel: "dashboard.trChannelPerformance.cyProfit",
  },
];

const calcYoyPercent = (currentValue, previousValue) => {
  const cy = Number(currentValue) || 0;
  const ly = Number(previousValue) || 0;
  if (ly === 0) return null;
  return _.round(((cy - ly) / ly) * 100, 1);
};

const MetricsCells = ({ metrics, formatter }) =>
  METRIC_GROUPS.map(({ key }) => (
    <React.Fragment key={key}>
      <td>
        <Text size="xs">{formatter.currency(metrics.ly?.[key] ?? 0)}</Text>
      </td>
      <td>
        <Text size="xs">{formatter.currency(metrics.cy?.[key] ?? 0)}</Text>
      </td>
      <td>
        <YoyGrowthCell value={metrics.yoy?.[key]} />
      </td>
    </React.Fragment>
  ));

const MetricsHeaderCells = ({ t }) =>
  METRIC_GROUPS.map(({ lyLabel, cyLabel, key }) => (
    <React.Fragment key={key}>
      <th>
        <Text size="xs" weight={700}>
          {t(lyLabel)}
        </Text>
      </th>
      <th>
        <Text size="xs" weight={700}>
          {t(cyLabel)}
        </Text>
      </th>
      <th>
        <Text size="xs" weight={700}>
          {t("dashboard.trChannelPerformance.yoyGrowth", "YoY Growth %")}
        </Text>
      </th>
    </React.Fragment>
  ));

const IndividualsTrChannelView = ({ channels, formatter, t, theme }) => {
  const chartData = useMemo(
    () =>
      channels.slice(0, TOP_CHART_LIMIT).map((channel) => ({
        name: channel.name,
        grossPremium: channel.cy?.grossPremium ?? 0,
      })),
    [channels]
  );

  const footerTotals = useMemo(() => {
    const ly = emptyMetrics();
    const cy = emptyMetrics();

    channels.forEach((channel) => {
      METRIC_GROUPS.forEach(({ key }) => {
        ly[key] += channel.ly?.[key] ?? 0;
        cy[key] += channel.cy?.[key] ?? 0;
      });
    });

    return {
      ly,
      cy,
      yoy: METRIC_GROUPS.reduce((acc, { key }) => {
        acc[key] = calcYoyPercent(cy[key], ly[key]);
        return acc;
      }, {}),
    };
  }, [channels]);

  const blueShades = [
    theme.colors.deepBlue?.[8] || "#1864ab",
    theme.colors.deepBlue?.[6] || "#1971c2",
    theme.colors.cyan?.[6] || "#15aabf",
    theme.colors.cyan?.[4] || "#3bc9db",
    theme.colors.cyan?.[2] || "#99e9f2",
  ];

  return (
    <>
      <BarChart
        title={t(
          "dashboard.trChannelPerformance.trPerformance",
          "TR Performance (Top 12)"
        )}
        height={Math.max(380, Math.min(600, chartData.length * 28))}
        data={chartData}
        useSampleData={false}
        chartProps={{
          keys: ["grossPremium"],
          indexBy: "name",
          layout: "vertical",
          colors: blueShades,
          enableLabel: true,
          labelSkipWidth: 12,
          labelSkipHeight: 12,
          valueFormat: (value) => formatCompactCurrency(value),
          margin: { top: 20, right: 40, bottom: 100, left: 60 },
          axisLeft: {
            format: (value) => formatCompactCurrency(value),
          },
          axisBottom: {
            tickRotation: -25,
            format: (value) =>
              value?.length > 14 ? `${value.substring(0, 14)}...` : value,
          },
        }}
      />

      <ScrollArea>
        <Table striped highlightOnHover withBorder>
          <thead>
            <tr>
              <th>
                <Text size="xs" weight={700}>
                  {t("dashboard.trChannelPerformance.trChannel", "TR/Channel")}
                </Text>
              </th>
              <MetricsHeaderCells t={t} />
            </tr>
          </thead>
          <tbody>
            {channels.map((channel) => (
              <tr key={channel.consultantId}>
                <td>
                  <Text size="xs">{channel.name}</Text>
                </td>
                <MetricsCells metrics={channel} formatter={formatter} />
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th>
                <Text size="xs" weight={700}>
                  {t("dashboard.trChannelPerformance.total", "Total")}
                </Text>
              </th>
              {METRIC_GROUPS.map(({ key }) => (
                <React.Fragment key={key}>
                  <th>
                    <Text size="xs">{formatter.currency(footerTotals.ly[key])}</Text>
                  </th>
                  <th>
                    <Text size="xs">{formatter.currency(footerTotals.cy[key])}</Text>
                  </th>
                  <th>
                    <YoyGrowthCell value={footerTotals.yoy[key]} />
                  </th>
                </React.Fragment>
              ))}
            </tr>
          </tfoot>
        </Table>
      </ScrollArea>
    </>
  );
};

const GroupedTrChannelView = ({
  groups,
  channels,
  nameColumnLabel,
  formatter,
  t,
  theme,
  excludeNoTeam = false,
}) => {
  const [expandedGroupId, setExpandedGroupId] = useState(null);

  const chartData = useMemo(() => {
    const visibleGroups = excludeNoTeam
      ? groups.filter((group) => group.groupId !== NO_TEAM_GROUP_ID)
      : groups;

    return visibleGroups.slice(0, TOP_CHART_LIMIT).map((group) => ({
      name: group.name,
      grossPremium: group.cy?.grossPremium ?? 0,
    }));
  }, [groups, excludeNoTeam]);

  const footerTotals = useMemo(() => buildFooterTotals(channels), [channels]);

  const blueShades = [
    theme.colors.deepBlue?.[8] || "#1864ab",
    theme.colors.deepBlue?.[6] || "#1971c2",
    theme.colors.cyan?.[6] || "#15aabf",
    theme.colors.cyan?.[4] || "#3bc9db",
    theme.colors.cyan?.[2] || "#99e9f2",
  ];

  return (
    <>
      <BarChart
        title={t(
          "dashboard.trChannelPerformance.trPerformance",
          "TR Performance (Top 12)"
        )}
        height={Math.max(380, Math.min(600, chartData.length * 28))}
        data={chartData}
        useSampleData={false}
        chartProps={{
          keys: ["grossPremium"],
          indexBy: "name",
          layout: "vertical",
          colors: blueShades,
          enableLabel: true,
          labelSkipWidth: 12,
          labelSkipHeight: 12,
          valueFormat: (value) => formatCompactCurrency(value),
          margin: { top: 20, right: 40, bottom: 100, left: 60 },
          axisLeft: {
            format: (value) => formatCompactCurrency(value),
          },
          axisBottom: {
            tickRotation: -25,
            format: (value) =>
              value?.length > 14 ? `${value.substring(0, 14)}...` : value,
          },
        }}
      />

      <ScrollArea>
        <Table striped highlightOnHover withBorder>
          <thead>
            <tr>
              <th>
                <Text size="xs" weight={700}>
                  {nameColumnLabel}
                </Text>
              </th>
              <MetricsHeaderCells t={t} />
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => {
              const isExpanded = expandedGroupId === group.groupId;

              return (
                <React.Fragment key={group.groupId}>
                  <tr
                    onClick={() =>
                      setExpandedGroupId((current) =>
                        current === group.groupId ? null : group.groupId
                      )
                    }
                    style={{ cursor: "pointer" }}
                  >
                    <td>
                      <Text size="xs" weight={600}>
                        {group.name}
                      </Text>
                    </td>
                    <MetricsCells metrics={group} formatter={formatter} />
                  </tr>
                  {isExpanded
                    ? group.members.map((member) => (
                        <tr key={member.consultantId}>
                          <td>
                            <Text size="xs" pl="md" color="dimmed">
                              {member.name}
                            </Text>
                          </td>
                          <MetricsCells metrics={member} formatter={formatter} />
                        </tr>
                      ))
                    : null}
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <th>
                <Text size="xs" weight={700}>
                  {t("dashboard.trChannelPerformance.total", "Total")}
                </Text>
              </th>
              {METRIC_GROUPS.map(({ key }) => (
                <React.Fragment key={key}>
                  <th>
                    <Text size="xs">{formatter.currency(footerTotals.ly[key])}</Text>
                  </th>
                  <th>
                    <Text size="xs">{formatter.currency(footerTotals.cy[key])}</Text>
                  </th>
                  <th>
                    <YoyGrowthCell value={footerTotals.yoy[key]} />
                  </th>
                </React.Fragment>
              ))}
            </tr>
          </tfoot>
        </Table>
      </ScrollArea>
    </>
  );
};

const DashboardTrChannelPerformanceTab = ({
  trChannelPerformance = {},
  loading = false,
}) => {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const [formatter] = useFormatter();
  const [viewMode, setViewMode] = useState("individual");
  const [excludeNoTeam, setExcludeNoTeam] = useState(true);

  const channels = trChannelPerformance?.channels || [];

  const viewOptions = useMemo(
    () => [
      {
        label: t("dashboard.trChannelPerformance.viewIndividual", "Individuals"),
        value: "individual",
      },
      {
        label: t("dashboard.trChannelPerformance.viewTeam", "Teams"),
        value: "team",
      },
      {
        label: t("dashboard.trChannelPerformance.viewLine", "Line"),
        value: "line",
      },
    ],
    [t]
  );

  const noTeamLabel = t(
    "dashboard.trChannelPerformance.noTeamSet",
    "No Team"
  );

  const teamGroups = useMemo(
    () => groupChannelsByTeam(channels, noTeamLabel),
    [channels, noTeamLabel]
  );

  const lineGroups = useMemo(
    () => groupChannelsByTopLine(channels),
    [channels]
  );

  return (
    <Box pos="relative" mt="50px">
      <SimpleLoadingIndicator visible={loading} />
      <Stack spacing="md">
        <Group spacing="sm" align="center">
          <SegmentedControl
            size="xs"
            value={viewMode}
            onChange={setViewMode}
            data={viewOptions}
          />
          {viewMode === "team" ? (
            <Checkbox
              size="xs"
              label={t(
                "dashboard.trChannelPerformance.excludeNoTeam",
                "Exclude No Team"
              )}
              checked={excludeNoTeam}
              onChange={(event) =>
                setExcludeNoTeam(event.currentTarget.checked)
              }
              styles={{ label: { fontSize: 12 } }}
            />
          ) : null}
        </Group>

        {viewMode === "individual" ? (
          <IndividualsTrChannelView
            channels={channels}
            formatter={formatter}
            t={t}
            theme={theme}
          />
        ) : null}

        {viewMode === "team" ? (
          <GroupedTrChannelView
            groups={teamGroups}
            channels={channels}
            nameColumnLabel={t("dashboard.trChannelPerformance.viewTeam", "Teams")}
            formatter={formatter}
            t={t}
            theme={theme}
            excludeNoTeam={excludeNoTeam}
          />
        ) : null}

        {viewMode === "line" ? (
          <GroupedTrChannelView
            groups={lineGroups}
            channels={channels}
            nameColumnLabel={t("dashboard.trChannelPerformance.viewLine", "Line")}
            formatter={formatter}
            t={t}
            theme={theme}
          />
        ) : null}
      </Stack>
    </Box>
  );
};

function emptyMetrics() {
  return {
    grossPremium: 0,
    brokerage: 0,
    trCommission: 0,
    profit: 0,
    policyCount: 0,
  };
}

export default DashboardTrChannelPerformanceTab;
